import { useState } from "react";

import {
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import {
  ClientAppEvent,
  EVOTRUX_BLUE,
  EVOTRUX_BORDER_GREY,
} from "constants/index";
import {
  buildTfcHistoryEntries,
  formatCentsWithCurrency,
  ISOStringToLocalReadableTimestamp,
} from "utils";

import { TfcHistoryDetailDialog } from "./TfcHistoryDetailDialog";

import type { TfcHistoryEntry } from "utils";

interface TfcHistorySectionProps {
  revisions: TfcRevision[];
  currency: string;
  bookedPriceCents: number;
}

export function TfcHistorySection(props: TfcHistorySectionProps) {
  const { revisions, currency, bookedPriceCents } = props;
  const [showHistory, setShowHistory] = useState(false);
  const [selected, setSelected] = useState<TfcHistoryEntry | null>(null);
  const posthog = usePostHog();

  const entries = buildTfcHistoryEntries(revisions);

  const handleToggle = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) posthog?.capture(ClientAppEvent.TFC_HISTORY_OPENED);
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="text"
          onClick={handleToggle}
          sx={{
            color: EVOTRUX_BLUE,
            fontWeight: 500,
            fontSize: "12px",
            textTransform: "uppercase",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {showHistory ? "HIDE HISTORY" : "VIEW HISTORY"}
        </Button>
      </Stack>

      {showHistory && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: `1px solid ${EVOTRUX_BORDER_GREY}`,
            borderRadius: "4px",
            mt: 1,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    py: "5px",
                    px: 2,
                  }}
                >
                  Action
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    py: "5px",
                    px: 2,
                  }}
                >
                  Total
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    py: "5px",
                    px: 2,
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    py: "5px",
                    px: 2,
                  }}
                >
                  By
                </TableCell>
                <TableCell sx={{ py: "5px", px: 2, width: "80px" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  onClick={() => setSelected(entry)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#F5F5F5" },
                  }}
                >
                  <TableCell sx={{ fontSize: "14px", py: "5px", px: 2 }}>
                    {entry.actionLabel}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", py: "5px", px: 2 }}>
                    {formatCentsWithCurrency(entry.totalCents, currency)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", py: "5px", px: 2 }}>
                    {ISOStringToLocalReadableTimestamp(entry.at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", py: "5px", px: 2 }}>
                    {entry.byName}
                  </TableCell>
                  <TableCell sx={{ py: "5px", px: 2, textAlign: "right" }}>
                    <Button
                      variant="text"
                      size="small"
                      aria-label={`View details for ${entry.actionLabel}`}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setSelected(entry);
                      }}
                      sx={{
                        fontSize: "14px",
                        color: EVOTRUX_BLUE,
                        textTransform: "none",
                        minWidth: 0,
                        p: 0,
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selected && (
        <TfcHistoryDetailDialog
          entry={selected}
          currency={currency}
          bookedPriceCents={bookedPriceCents}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
